/**
 * Ledger's DApp Browser (v3) injects its EIP-1193 provider as `window.ethereum`
 * *and* announces the same provider over EIP-6963 under the `com.ledger` rdns.
 * wagmi turns the EIP-6963 announcement into its own connector, so a single
 * Ledger session can surface as either connector depending on which one wins
 * the mount race — both have to be treated as "connected through Ledger".
 *
 * The provider carries an `isLedgerLive` flag, which is Ledger's documented way
 * of detecting the in-app browser and enabling auto-connection.
 *
 * https://developers.ledger.com/docs/ledger-live/discover/integration/dapp-browser/dapp-customisation
 */

import type { EIP1193Provider } from "viem";

/** Connector id wagmi derives from Ledger's EIP-6963 rdns. */
export const LEDGER_RDNS = "com.ledger";

/** Id of the injected-provider fallback connector registered in WagmiProvider. */
export const LEDGER_INJECTED_CONNECTOR_ID = "ledgerInjected";

export const isLedgerProvider = (provider: unknown): boolean =>
  Boolean((provider as { isLedgerLive?: boolean } | undefined)?.isLedgerLive);

/**
 * `window.ethereum`, but only when it is Ledger's provider. Takes the window
 * structurally so it can be passed straight to wagmi's `injected({ target })`
 * provider resolver, which supplies its own `Window` type.
 */
export const getLedgerInjectedProvider = (
  target: { ethereum?: EIP1193Provider } | undefined = typeof window ===
  "undefined"
    ? undefined
    : (window as unknown as { ethereum?: EIP1193Provider })
): EIP1193Provider | undefined =>
  isLedgerProvider(target?.ethereum) ? target?.ethereum : undefined;

export const isLedgerConnectorId = (id?: string): boolean =>
  id === LEDGER_RDNS || id === LEDGER_INJECTED_CONNECTOR_ID;
